import { NextRequest, NextResponse, userAgent } from "next/server";

import { ipAddress } from "@vercel/functions";
import {
  COOKIEBOT_CONSENT_COOKIE,
  COOKIEBOT_CONSENTS,
} from "@/legal/cookie-consent/consts";
import { isUserConsentGranted } from "@/legal/cookie-consent/isUserConsentGranted";

export const getUniverseFromHost = (
  host: string,
  universeTags?: string[],
): string | null => {
  const topDomain = host.split(".").slice(-2).join(".");
  const sortedTags = [...(universeTags ?? [])].sort(
    (a, b) => b.length - a.length,
  );
  const matcher = new RegExp(`(?<tag>${sortedTags.join("|")})`, "gi");
  const matches = topDomain.match(matcher);
  if (!matches) return null;
  const universe = `${matches[0]}`.toLowerCase();
  if (universe === "") return null;
  return universe;
};

export const COOKIE_UNIVERSE = "universe";
export const COOKIE_INTERNAL_USER_OR_BOT = "cada-is-internal-user-or-bot";
export const COOKIE_CAMPAIGN_SOURCE = "cada-campaign";

export function getUniverseCookie({ cookies }: Pick<NextRequest, "cookies">) {
  return cookies.get(COOKIE_UNIVERSE)?.value ?? null;
}

export function getUniverse(request: NextRequest, universeTags: string[]) {
  const universeTag =
    getUniverseFromHost(request.nextUrl.host, universeTags) ??
    getUniverseCookie(request);
  return universeTag;
}

const getQueryParams = (searchParams: URLSearchParams) => {
  if (searchParams.toString().length > 0) {
    return `?${searchParams}`;
  }
  return "";
};

export const INTERNAL_USER_DETECTION = {
  HEADERS: ["cada-playwright-runner"],
  IP_ADDRESSES: ["188.95.241.*", "188.95.247.*"], // @TODO: Update when we move!
  BOTS: ["bot"],
} as const;

const CAMPAIGN_SOURCE_PARAMS = [
  "campaign",
  "campaignCodeId",
  "promotionCode",
  "PromotionCode",
  "promotionCodeId",
  "PromotionCodeId",
] as const;

function getCampaignSource(searchParams: URLSearchParams) {
  for (const param of CAMPAIGN_SOURCE_PARAMS) {
    const value = searchParams.get(param);
    if (value) return value;
  }
  return null;
}

export const isIpMatch = (pattern: string, ip?: string) =>
  Boolean(ip?.match(new RegExp(pattern)));

export const getInternalUserOrBot = (request: NextRequest) => {
  const ip = ipAddress(request);
  const { isBot } = userAgent(request);

  return (
    INTERNAL_USER_DETECTION.HEADERS.find((header) =>
      Boolean(request.headers.get(header)),
    ) ??
    INTERNAL_USER_DETECTION.IP_ADDRESSES.find((ipAddress) =>
      isIpMatch(ipAddress, ip),
    ) ??
    INTERNAL_USER_DETECTION.BOTS.find(() => isBot) ??
    null
  );
};

export function handleInternalUserOrBot(
  request: NextRequest,
  response: NextResponse,
) {
  const internalUserOrBot = getInternalUserOrBot(request);
  if (internalUserOrBot) {
    const existing = request.cookies.get(COOKIE_INTERNAL_USER_OR_BOT)?.value;
    if (existing !== JSON.stringify(internalUserOrBot)) {
      response.cookies.set(
        COOKIE_INTERNAL_USER_OR_BOT,
        JSON.stringify(internalUserOrBot),
      );
    }
  } else if (request.cookies.has(COOKIE_INTERNAL_USER_OR_BOT)) {
    response.cookies.delete(COOKIE_INTERNAL_USER_OR_BOT);
  }
  return response;
}

export function handleMarketing(request: NextRequest, response: NextResponse) {
  const cookieConsent = request.cookies.get(COOKIEBOT_CONSENT_COOKIE)?.value;
  if (isUserConsentGranted(COOKIEBOT_CONSENTS.MARKETING, cookieConsent)) {
    const campaignSourceParam = getCampaignSource(request.nextUrl.searchParams);
    const campaignSourceCookie = request.cookies.get(
      COOKIE_CAMPAIGN_SOURCE,
    )?.value;

    if (campaignSourceParam && campaignSourceParam !== campaignSourceCookie) {
      response.cookies.set(COOKIE_CAMPAIGN_SOURCE, campaignSourceParam);
    }
  } else if (request.cookies.has(COOKIE_CAMPAIGN_SOURCE)) {
    response.cookies.delete(COOKIE_CAMPAIGN_SOURCE);
  }
  return response;
}

export function resolveSitePage(
  request: NextRequest,
  universeTag: string | null,
  code: string,
) {
  const {
    nextUrl: { pathname, searchParams },
  } = request;

  if (!universeTag) return NextResponse.next();

  const queryParams = getQueryParams(searchParams);
  const url = `/${code}/${universeTag.replace("/", "")}${pathname}${queryParams}`;

  const response = NextResponse.rewrite(new URL(url, request.url));
  if (
    request.cookies.get(COOKIE_UNIVERSE)?.value !== universeTag.replace("/", "")
  ) {
    response.cookies.set(COOKIE_UNIVERSE, universeTag.replace("/", ""));
  }

  return response;
}
